import { useEffect, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { api } from '../../lib/api/client.js';
import { CardHead, ErrorBanner } from '../../components/ui/Primitives.js';

/**
 * Where Microsoft sends an admin back after "Connect Microsoft 365" (registered on the ArcNet for Teams
 * app as https://tms.arcnet.co.za/teams/connected). A signed-in page, not an API route: it hands
 * Microsoft's answer and the state we issued to the API, which checks both and proves the consent by
 * getting a token in that tenant (teams/microsoft.ts). Then back to the account's Microsoft Teams page.
 *
 * Posted once, however often React renders, so a refresh of this page never connects twice.
 */
export function TeamsConnectedPage() {
  const router = useRouter();
  const sent = useRef(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    const q = new URLSearchParams(window.location.search);
    const body: Record<string, string> = {};
    for (const k of ['state', 'tenant', 'admin_consent', 'error', 'error_description']) {
      const v = q.get(k);
      if (v) body[k] = v;
    }
    // Only the visit carrying Microsoft's answer sends it. The address is left alone until the page
    // moves on: rewriting it here remounted the page, which then posted an empty answer and showed its
    // refusal over a connection that had just succeeded (owner, 21 October 2026).
    if (!body.state) { setError(new Error('Nothing to finish here: start Connect Microsoft 365 from the account.')); return; }
    api.post<{ tenant_id: string }>('/v1/teams/microsoft/connected', body)
      .then((r) => router.history.replace(`/manage/${r.tenant_id}/teams`))
      .catch((e: unknown) => setError(e));
  }, [router]);

  return (
    <div style={{ maxWidth: 640, margin: "24px auto" }}>
      <div className="arc-card">
        <CardHead title="Connecting Microsoft 365" />
        <div className="arc-card-body">
          {error ? (
            <>
              <ErrorBanner error={error} />
              <p className="arc-hint" style={{ marginTop: 12 }}>
                Go back to the account&apos;s Microsoft Teams page and press Connect Microsoft 365 again.
              </p>
            </>
          ) : (
            <p className="arc-muted" style={{ margin: 0 }}>Checking with Microsoft…</p>
          )}
        </div>
      </div>
    </div>
  );
}
